import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaUpload, FaTrash, FaEdit, FaSave, FaTimes } from 'react-icons/fa';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import API_BASE_URL from '../config';
import './Admin.css';

const TrackManager = ({ onTrackAdded }) => {
  const { t } = useLanguage();
  const [tracks, setTracks] = useState([]);
  const [editingTrack, setEditingTrack] = useState(null);
  const [trackForm, setTrackForm] = useState({
    title: '',
    artist: '',
    project: 'commercial',
    music: null
  });
  const [trackUploading, setTrackUploading] = useState(false);
  const [trackUploadProgress, setTrackUploadProgress] = useState(0);
  const [draggedTrackId, setDraggedTrackId] = useState(null);
  const [dragOverTrackId, setDragOverTrackId] = useState(null);

  useEffect(() => {
    fetchTracks();
  }, []);

  const fetchTracks = async () => {
    try {
      console.log('🔄 Fetching tracks from server...');
      const response = await axios.get(`${API_BASE_URL}/api/tracks`);
      console.log('✅ Received tracks:', response.data.map(t => ({ id: t.id, title: t.title })));
      setTracks(response.data);
    } catch (error) {
      console.error('❌ Error fetching tracks:', error);
    }
  };

  const handleTrackUpload = async (e) => {
    e.preventDefault();
    if (!trackForm.music) {
      alert(t('admin.selectFile'));
      return;
    }

    const formData = new FormData();
    formData.append('music', trackForm.music);
    formData.append('title', trackForm.title);
    formData.append('artist', trackForm.artist);
    formData.append('project', trackForm.project);

    setTrackUploading(true);
    setTrackUploadProgress(0);

    try {
      await axios.post(`${API_BASE_URL}/api/tracks`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setTrackUploadProgress(percentCompleted);
          }
        }
      });
      setTrackForm({ title: '', artist: '', project: 'commercial', music: null });
      setTrackUploadProgress(0);
      fetchTracks();
      if (onTrackAdded) onTrackAdded();
      alert(t('admin.trackUploaded'));
    } catch (error) {
      console.error('Error uploading track:', error);
      alert(t('admin.trackUploadError'));
    } finally {
      setTrackUploading(false);
      setTimeout(() => setTrackUploadProgress(0), 1000);
    }
  };

  const handleDeleteTrack = async (id) => {
    if (!window.confirm(t('admin.deleteTrackConfirm'))) return;

    try {
      await axios.delete(`${API_BASE_URL}/api/tracks/${id}`);
      fetchTracks();
      if (onTrackAdded) onTrackAdded();
    } catch (error) {
      console.error('Error deleting track:', error);
      alert(t('admin.trackDeleteError'));
    }
  };

  const handleSaveTrack = async (id) => {
    try {
      const title = document.getElementById(`track-title-${id}`).value.trim();
      const artist = document.getElementById(`track-artist-${id}`).value.trim();
      const project = document.getElementById(`track-project-${id}`).value;

      if (!title || !artist) {
        alert(t('admin.fillAllFields'));
        return;
      }

      await axios.put(`${API_BASE_URL}/api/tracks/${id}`, {
        title,
        artist,
        project
      });

      setEditingTrack(null);
      fetchTracks();
    } catch (error) {
      console.error('Error updating track:', error);
      alert(t('admin.trackUpdateError'));
    }
  };

  // Drag & Drop handlers
  const handleDragStart = (e, trackId) => {
    if (editingTrack === trackId) {
      e.preventDefault();
      return;
    }
    setDraggedTrackId(trackId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', trackId);
  };

  const handleDragEnd = () => {
    setDraggedTrackId(null);
    setDragOverTrackId(null);
  };

  const handleDragOver = (e, trackId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (trackId !== draggedTrackId) {
      setDragOverTrackId(trackId);
    }
  };

  const handleDragLeave = () => {
    setDragOverTrackId(null);
  };

  const handleDrop = async (e, targetTrackId) => {
    e.preventDefault();
    setDragOverTrackId(null);
    
    if (!draggedTrackId || draggedTrackId === targetTrackId) {
      console.log('⚠️ Invalid drop: draggedTrackId or targetTrackId missing, or they are the same');
      return;
    }

    console.log('🎯 Drop event:', { draggedTrackId, targetTrackId });
    console.log('📋 Current tracks:', tracks.map(t => ({ id: t.id, title: t.title })));

    const draggedIndex = tracks.findIndex(t => String(t.id) === String(draggedTrackId));
    const targetIndex = tracks.findIndex(t => String(t.id) === String(targetTrackId));
    
    console.log('🔍 Found indices:', { draggedIndex, targetIndex });
    
    if (draggedIndex === -1 || targetIndex === -1) {
      console.error('❌ Track not found in local state:', { 
        draggedTrackId, 
        targetTrackId, 
        draggedIndex, 
        targetIndex,
        availableIds: tracks.map(t => t.id)
      });
      await fetchTracks();
      return;
    }

    const newTracks = [...tracks];
    const [removed] = newTracks.splice(draggedIndex, 1);
    newTracks.splice(targetIndex, 0, removed);

    console.log('📦 New order:', newTracks.map(t => ({ id: t.id, title: t.title })));

    setTracks(newTracks);

    const trackIds = newTracks.map(t => String(t.id));
    
    try {
      console.log('🔄 Sending track order update...');
      console.log('📤 Track IDs to send:', trackIds);
      console.log('📋 Current tracks:', tracks.map(t => ({ id: t.id, idType: typeof t.id, title: t.title })));
      console.log('📋 New tracks order:', newTracks.map(t => ({ id: t.id, idType: typeof t.id, title: t.title })));
      console.log(`🌐 Request URL: ${API_BASE_URL}/api/tracks/order`);
      console.log('📦 Request body:', { trackIds });
      
      const response = await axios.put(`${API_BASE_URL}/api/tracks/order`, { trackIds });
      console.log('✅ Track order updated successfully:', response.data);
      if (onTrackAdded) onTrackAdded();
    } catch (error) {
      console.error('❌ Error updating track order:', error);
      console.error('📋 Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText,
        url: error.config?.url,
        method: error.config?.method,
        trackIds: trackIds,
        trackIdsTypes: trackIds.map(id => typeof id)
      });
      
      fetchTracks();
      
      let errorMessage = t('admin.orderUpdateError');
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
        if (error.response.data.missingIds) {
          errorMessage += ` Missing IDs: ${error.response.data.missingIds.join(', ')}`;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      alert(`${t('admin.orderUpdateError')}: ${errorMessage}`);
    }
  };

  return (
    <div className="admin-section">
      <h2>{t('admin.uploadNewTrack')}</h2>
      <form onSubmit={handleTrackUpload} className="upload-form">
        <input
          type="text"
          placeholder={t('admin.trackTitle')}
          value={trackForm.title}
          onChange={(e) => setTrackForm({ ...trackForm, title: e.target.value })}
          required
        />
        <input
          type="text"
          placeholder={t('admin.artist')}
          value={trackForm.artist}
          onChange={(e) => setTrackForm({ ...trackForm, artist: e.target.value })}
          required
        />
        <select
          value={trackForm.project}
          onChange={(e) => setTrackForm({ ...trackForm, project: e.target.value })}
        >
          <option value="commercial">Commercial</option>
          <option value="underground">Underground</option>
        </select>
        <label className="file-input-label">
          <FaUpload /> {t('admin.selectMusicFile')}
          <input
            type="file"
            accept="audio/*"
            onChange={(e) => setTrackForm({ ...trackForm, music: e.target.files[0] })}
          />
          {trackForm.music && (
            <span>
              {trackForm.music.name} 
              {trackForm.music.size && (
                <span className="file-size">
                  {' '}({(trackForm.music.size / (1024 * 1024)).toFixed(2)} MB)
                </span>
              )}
            </span>
          )}
        </label>
        
        {trackUploading && (
          <div className="upload-progress-container">
            <div className="upload-progress-bar">
              <div 
                className="upload-progress-fill" 
                style={{ width: `${trackUploadProgress}%` }}
              ></div>
            </div>
            <div className="upload-progress-text">
              {trackUploadProgress}% {trackUploadProgress < 100 ? t('admin.uploading') : t('admin.processing')}
            </div>
          </div>
        )}
        
        <button type="submit" disabled={trackUploading}>
          {trackUploading ? t('admin.uploading') : t('admin.upload')}
        </button>
      </form>

      <h2>{t('admin.existingTracks')}</h2>
      <div className="items-list">
        {tracks.map((track) => (
          <motion.div
            key={track.id}
            className={`item-card track-item-card ${draggedTrackId === track.id ? 'dragging' : ''} ${dragOverTrackId === track.id ? 'drag-over' : ''}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            draggable={editingTrack !== track.id}
            onDragStart={(e) => handleDragStart(e, track.id)}
            onDragEnd={handleDragEnd}
            onDragOver={(e) => handleDragOver(e, track.id)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, track.id)}
          >
            {editingTrack === track.id ? (
              <div className="edit-form">
                <input
                  type="text"
                  defaultValue={track.title}
                  id={`track-title-${track.id}`}
                  placeholder={t('admin.trackTitle')}
                />
                <input
                  type="text"
                  defaultValue={track.artist}
                  id={`track-artist-${track.id}`}
                  placeholder={t('admin.artist')}
                />
                <select
                  defaultValue={track.project}
                  id={`track-project-${track.id}`}
                >
                  <option value="commercial">Commercial</option>
                  <option value="underground">Underground</option>
                </select>
                <div className="edit-actions">
                  <button
                    className="save-btn"
                    onClick={() => handleSaveTrack(track.id)}
                  >
                    <FaSave /> {t('admin.save')}
                  </button>
                  <button
                    className="cancel-btn"
                    onClick={() => setEditingTrack(null)}
                  >
                    <FaTimes /> {t('admin.cancel')}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="item-info">
                  <div className="drag-handle" title={t('admin.dragToReorder')}>
                    <span className="drag-icon">⋮⋮</span>
                  </div>
                  <div className="item-text-info">
                    <h3>{track.title}</h3>
                    <p>
                      <strong>{track.artist}</strong>
                    </p>
                  </div>
                </div>
                <div className="item-footer">
                  <span className={`project-badge ${track.project}`}>
                    {track.project === 'commercial' ? 'Commercial' : 'Underground'}
                  </span>
                  <div className="item-actions">
                    <button
                      className="edit-btn"
                      onClick={() => setEditingTrack(track.id)}
                      title={t('admin.edit')}
                    >
                      <FaEdit />
                    </button>
                    <button
                      className="delete-btn"
                      onClick={() => handleDeleteTrack(track.id)}
                      title={t('admin.delete')}
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default TrackManager;

